/* Small building blocks shared by the Access panels, dialog and sheet. */
import type { ReactNode } from 'react';
import type { Project } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { Kicker } from '@/components/Kicker';
import { Tag } from '@/components/badges';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  title?: string;
  disabled?: boolean;
}

/** Full-width segmented control for 2–3 mutually exclusive choices (one row at every width). */
export function Segmented<T extends string>({ label, testId, value, onChange, options }: { label: string; testId: string; value: T; onChange: (v: T) => void; options: Array<SegmentedOption<T>> }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span id={`${testId}-label`} className="text-[13px] font-medium leading-none text-foreground">
        {label}
      </span>
      <div role="radiogroup" aria-labelledby={`${testId}-label`} className={cn('grid gap-1 rounded-lg border bg-secondary p-1', options.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            disabled={o.disabled}
            title={o.title}
            data-testid={`${testId}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex h-8 min-w-0 items-center justify-center rounded-md px-2 text-[13px] font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-10',
              value === o.value ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <span className="truncate">{o.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Scopes as mono chips; `max` folds the rest into "+N" (the full list stays in its title). */
export function ScopeChips({ scopes, max, className }: { scopes: string[]; max?: number; className?: string }) {
  const shown = max ? scopes.slice(0, max) : scopes;
  const rest = scopes.length - shown.length;
  if (scopes.length === 0) return <span className="text-xs text-subtle">—</span>;
  return (
    <span className={cn('flex min-w-0 flex-wrap gap-1', className)}>
      {shown.map((s) => (
        <Tag key={s} className={s === 'admin' ? 'text-awaiting' : 'text-muted-foreground'}>
          {s}
        </Tag>
      ))}
      {rest > 0 ? <Tag title={scopes.slice(shown.length).join(', ')}>+{rest}</Tag> : null}
    </span>
  );
}

/** `null` → "All projects"; ids → their slugs (an id this console cannot see stays a short id). */
export function projectsLabel(projectIds: string[] | null | undefined, projects: Project[] | undefined): string {
  if (!projectIds || projectIds.length === 0) return 'All projects';
  return projectIds.map((id) => projects?.find((p) => p.id === id)?.slug ?? `${id.slice(0, 8)}…`).join(', ');
}

/** The compact (embedded) container: a bordered sub-panel with a mono kicker, as in screen 27 "CLI & CI access". */
export function SubPanel({ title, meta, action, children, testId, className }: { title: string; meta?: ReactNode; action?: ReactNode; children: ReactNode; testId?: string; className?: string }) {
  return (
    <section data-testid={testId} aria-label={title} className={cn('flex min-w-0 flex-col gap-3 rounded-lg border bg-secondary/40 p-4', className)}>
      <header className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <Kicker className="font-mono text-[11px] tracking-[0.1em]">{title}</Kicker>
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          {meta}
          {action}
        </span>
      </header>
      {children}
    </section>
  );
}
